import { Component, Event, EventEmitter, h, Method, Prop } from "@stencil/core";
import { TextFieldProps } from "../f-text-field/f-text-field-model";

@Component({
  tag: "f-search-field",
  shadow: true,
})
export class SearchField {
  private textField: HTMLFTextFieldElement;

  @Prop() propertyId: TextFieldProps["propertyId"];
  @Prop() name: TextFieldProps["name"];
  @Prop({ mutable: true }) disabled: TextFieldProps["disabled"] = false;
  @Prop() label: TextFieldProps["label"];
  @Prop() placeholder: TextFieldProps["placeholder"];
  @Prop() reset: TextFieldProps["reset"] = true;
  @Prop() isValid: TextFieldProps["isValid"];
  @Prop() closeButtonHiddenLabel: TextFieldProps["label"];
  @Prop() submitSearchHiddenLabel: TextFieldProps["label"];
  @Prop() inputHiddenLabel: TextFieldProps["hiddenLabel"];

  @Prop({ mutable: true }) value: TextFieldProps["value"] = "";

  @Event({ eventName: "searchFieldChanged" })
  searchFieldChanged: EventEmitter<{
    fieldId: string;
    value: string;
    valid: boolean;
  }>;

  @Event({ eventName: "searchFieldSubmitted" })
  searchFieldSubmitted: EventEmitter<{
    fieldId: string;
    value: string;
    valid: boolean;
  }>;

  @Event({ eventName: "searchFieldReset" })
  searchFieldReset: EventEmitter<{
    fieldId: string;
    value: string;
    valid: boolean;
  }>;

  @Method()
  async focusOnSearchField() {
    await customElements.whenDefined("f-text-field");
    await this.textField.focusOnTextField();
  }

  private handleSearchFieldSubmit = (): void => {
    this.searchFieldSubmitted.emit({
      fieldId: this.propertyId,
      value: this.value,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
    });
  };

  private handleSearchFieldChange = (event: InputEvent): void => {
    this.value = (event.target as HTMLInputElement).value;
    this.searchFieldChanged.emit({
      fieldId: this.propertyId,
      value: this.value,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
    });
  };

  private handleSearchFieldReset = (): void => {
    this.value = "";
    this.searchFieldReset.emit({
      fieldId: this.propertyId,
      value: this.value,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
    });
  };

  render() {
    return (
      <f-text-field-group disabled={this.disabled} data-test="search-field">
        <f-text-field
          label={this.label}
          type="search"
          value={this.value}
          propertyId={this.propertyId}
          name={this.name}
          class="a-text-field__input"
          disabled={this.disabled}
          placeholder={this.placeholder}
          onInput={this.handleSearchFieldChange}
          ref={(el) => (this.textField = el)}
          data-test="search-field-text"
          hiddenLabel={this.inputHiddenLabel}
        />

        <f-button
          type="submit"
          disabled={this.disabled}
          variant="integrated"
          iconOnly={true}
          additionalProps={{
            "aria-haspopup": false,
            "aria-expanded": false,
          }}
          onClick={this.handleSearchFieldSubmit}
          data-test="search-field-button-submit"
        >
          <f-visually-hidden>{this.submitSearchHiddenLabel}</f-visually-hidden>
          <f-icon iconName="search" data-test="search-field-icon-submit" />
        </f-button>

        {this.reset ? (
          <f-button
            type="reset"
            variant="integrated"
            additionalProps={{
              "aria-haspopup": false,
              "aria-expanded": false,
            }}
            iconOnly={true}
            disabled={this.disabled}
            onClick={this.handleSearchFieldReset}
            data-test="search-field-button-reset"
          >
            <f-visually-hidden>{this.closeButtonHiddenLabel}</f-visually-hidden>
            <f-icon
              iconName="close-small"
              data-test="search-field-icon-reset"
            />
          </f-button>
        ) : (
          ""
        )}
      </f-text-field-group>
    );
  }
}
